import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import moment from 'moment';

import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import DatePicker from '@/shared/components/input/DatePicker';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { falseFunc, isBeforeMinimum } from '@/shared/utils/validationHelpers';
import messages from './taskMessages';

// One date field per global action. The key is also the property name the batch endpoint expects, so
// adding a third field here is a matter of one entry plus its endpoint.
const fields = {
  deadline: {
    title: messages.ChangeSubtaskDeadline,
    label: messages.Deadline,
    inputId: 'taskDeadline',
  },
  executionDate: {
    title: messages.ChangeSubtaskExecutionDate,
    label: messages.ExecutionDate,
    inputId: 'subtaskExecutionDate',
  },
};

export const subtaskFields = Object.keys(fields);

export default class ChangeSubtaskFieldModal extends Component {
  state = {
    date: moment.utc(),
    selectedSubtask: null,
  };

  componentDidMount() {
    this.form = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { show } = this.props;

    if (prevProps.show !== show) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(() => ({
        date: moment.utc(),
        showValidation: false,
        selectedSubtask: null,
      }));
    }
  }

  static propTypes = {
    field: PropTypes.oneOf(subtaskFields).isRequired,
    onSave: PropTypes.func.isRequired,
    subtasks: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }))
      .isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
  };

  static defaultProps = {
    show: false,
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  validate() {
    if (!this.form || !this.form.current) return false;
    const { selectedSubtask, date } = this.state;
    if (!selectedSubtask || !selectedSubtask.id) return false;
    if (isBeforeMinimum(date)) return false;

    return this.form.current.reportValidity();
  }

  dateChanged = input => {
    this.setState({
      date: input,
    });
  };
  subtaskChanged = event => {
    const { subtasks } = this.props;
    const subtaskId = event.target.value;
    const subtask = subtasks.find(x => x.id === subtaskId);

    this.setState({
      selectedSubtask: subtask,
    });
  };
  onSave = () => {
    const { date, selectedSubtask } = this.state;
    const { onSave, field } = this.props;
    if (!this.validate()) return Promise.reject(new Error('invalid form'));

    const parameters = {
      subtaskTemplateId: selectedSubtask.id,
      [field]: date,
    };

    return onSave(parameters);
  };

  render() {
    const { show, intl, subtasks, field } = this.props;
    const { date, dateFocused, selectedSubtask, showValidation } = this.state;
    const { title, label, inputId } = fields[field];

    return (
      <OkCancelModal
        title={intl.formatMessage(title)}
        show={show}
        onClose={this.closeModal}
        disabled={showValidation && !this.validate()}
        onSave={this.onSave}
        onHoverSave={() => this.setState({ showValidation: true })}
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              <div
                className={classNames('form-group', {
                  'has-error': showValidation && (!selectedSubtask || !selectedSubtask.id),
                })}
              >
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.Subtask)}
                </label>
                <div className="col-md-6">
                  {subtasks && (
                    <select
                      onChange={this.subtaskChanged}
                      className="form-control form-field"
                      value={(selectedSubtask && selectedSubtask.id) || ''}
                    >
                      {!selectedSubtask && (
                        <option value="">{intl.formatMessage(messages.SelectASubtask)}</option>
                      )}
                      {subtasks.map(subtask => (
                        <option key={subtask.id} value={subtask.id}>
                          {intl.formatMessage(
                            listItemMsg('taskTemplate', subtask.id, subtask.name),
                          )}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div
                className={classNames('form-group', {
                  'has-error': showValidation && isBeforeMinimum(date),
                })}
              >
                <label className="control-label col-sm-3">
                  <FormattedMessage {...label} />
                </label>
                <div className="col-md-6">
                  <DatePicker
                    placeholder={intl.formatMessage(label)}
                    date={date}
                    onDateChange={this.dateChanged}
                    focused={dateFocused}
                    onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                    id={inputId}
                    block
                    small
                    isOutsideRange={falseFunc}
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}
